/**
 * AI Model Capabilities Schema
 *
 * Defines what a model can do - used for filtering models by capability
 * across both the sync tool and backend API.
 *
 * Examples:
 * - Image capabilities: 'text-to-image', 'inpainting', 'upscaling'
 * - Video capabilities: 'text-to-video', 'image-to-video'
 * - Audio capabilities: 'text-to-speech', 'audio-to-audio'
 *
 * @module @genieartinc/schemas/pools/ai
 */

import { z } from 'zod';

/**
 * Model capabilities enum
 *
 * Represents specific capabilities that AI models can have.
 * A model can have multiple capabilities (e.g., 'inpainting' + 'image-to-image').
 *
 * Used for:
 * - Filtering models in UI (e.g., show only inpainting models)
 * - Categorizing models in sync tool
 * - Validating model configuration
 */
export const ModelCapabilitySchema = z.enum([
  // Image generation capabilities
  'text-to-image',
  'image-to-image',
  'inpainting',
  'outpainting',
  'upscaling',
  'background-removal',
  'controlnet',
  'image-to-3d',

  // Video generation capabilities
  'text-to-video',
  'image-to-video',
  'video-to-video',

  // Audio capabilities
  'text-to-audio',
  'text-to-speech',
  'audio-to-audio',
  'audio-to-video',

  // Text capabilities
  'text-to-text',
  'chat',
  'completion',

  // 3D capabilities
  'text-to-3d',
  '3d-to-3d',

  // Training
  'training',
  'fine-tuning',
  'lora',

  // Image tool capabilities (from real API tags)
  'relighting',
  'photo-restoration',
  'enhancement',
  'object-removal',
  'headshot',
  'makeup',
  'age-transformation',
  'hair-edit',
  'face-editing',
  'expression-change',
  'perspective-change',
  'style-transfer',
  'image-editing',

  // Video tool capabilities (from real API tags)
  'video-editing',
  'lipsync',
  'video-upscaling',
  'motion',
  'effects',
  'animation',

  // Multi-modal capabilities
  'multimodal',
  'vision',

  // Specialized capabilities (from real API tags)
  'realism',
  'high-res',
  'typography',
  'vector',
  'product',
  'fashion',
  'marketing',
]);

/**
 * Type inference for ModelCapability
 *
 * Usage:
 * ```typescript
 * const capability: ModelCapability = 'inpainting';
 * const capabilities: ModelCapability[] = ['text-to-image', 'image-to-image'];
 * ```
 */
export type ModelCapability = z.infer<typeof ModelCapabilitySchema>;

/**
 * Array of model capabilities schema
 *
 * Used in model config to define multiple capabilities
 */
export const ModelCapabilitiesArraySchema = z.array(ModelCapabilitySchema).default([]);
